import type { ISearchHit } from "commonComponents/utilities/MainNavigation/SearchDialog/search/search.types";

import { getEntityMeta } from "./entityMeta";

/**
 * Strip markdown bold markers (**text**) from a string,
 * returning the plain text content.
 */
export function stripMarkdownBold(text: string): string {
    return text.replace(/\*\*(.*?)\*\*/g, "$1");
}

/**
 * Choose the subtitle (sub-text) shown under a search result's title.
 *
 * The job name is always preferred so users can tell which job a result
 * belongs to. It is returned bare: a name without a label reads as the job.
 * The search service renders the body as newline-separated "Label: value" lines (the first line being the bold title); the job is
 * rendered as a "Job: <name>" line. Some entities (e.g. Bids, Bills, RFIs,
 * Submittals) list it after other fields rather than first, so we search the
 * whole body for it rather than assuming it's the first line. When no job is
 * present (e.g. Documents), we fall back to the first body field with a value.
 */
// Matches the job line in a rendered body. The service uses the explicit "Job"
// label today; "Job Name" is the label auto-derived from the `job_name` field
// when a spec doesn't override it, so we accept both. Anchored + the optional
// " name" only, so it won't catch unrelated labels like "Jobsite:".
const JOB_LABEL = /^job(?:\s+name)?:\s*/i;

export function extractSubtitle(hit: ISearchHit): string | null {
    // The job name may arrive as a structured payload field (future-proofing —
    // the service currently only sends it inside the body) under either casing.
    // Guard the type: payload values are `unknown`, and calling .trim() on a
    // non-string would throw and break the whole result card.
    const rawJob = hit.payload?.jobName ?? hit.payload?.job_name;
    if (typeof rawJob === "string" && rawJob.trim().length > 0) {
        return rawJob.trim();
    }

    if (hit.summary) {
        const lines = hit.summary
            .split("\n")
            .map((l) => stripMarkdownBold(l).trim())
            .filter((l) => l.length > 0);

        // The job isn't always the first body field (e.g. Bids/Bills/RFIs/
        // Submittals list it after other fields), so scan the whole body. The
        // first match wins — which is the real job_name field, since it's
        // emitted before any free-text fields that might also contain "Job:".
        const jobLine = lines.find((l) => JOB_LABEL.test(l));
        if (jobLine) {
            const jobValue = jobLine.replace(JOB_LABEL, "").trim();
            if (jobValue.length > 0) return jobValue;
        }

        // Fallback: the first body field after the title that has a value.
        const fallback = lines.slice(1).find((l) => {
            const colon = l.indexOf(":");
            return colon === -1 || l.slice(colon + 1).trim().length > 0;
        });
        if (fallback) return fallback;
    }
    return null;
}

/**
 * The row title for a hit: the first line of the search service's summary,
 * falling back to "<Entity> #<id>" when the summary is empty or is only markdown
 * emphasis. Shared so every surface titles the same record the same way.
 */
export function extractTitle(hit: ISearchHit): string {
    if (hit.summary) {
        const firstLine = hit.summary.split("\n")[0].trim();
        if (firstLine.length > 0) {
            const cleaned = stripMarkdownBold(firstLine);
            if (cleaned.trim().length > 0) return cleaned;
        }
    }
    const meta = getEntityMeta(hit.entity);
    return `${meta.label} #${hit.id}`;
}
